import React from "react"
import ReactDOM from "react-dom/client"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "react-router-dom"
import { ClerkProvider } from "@clerk/clerk-react"
import "./styles/base.css"
import "./styles/design-tokens.css"
import { initializeStoredTheme, ThemeProvider } from "./theme/ThemeProvider"
import router from "./router"
import { DemoProvider } from "./demo/DemoProvider"

const queryClient = new QueryClient()
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const appMode = import.meta.env.MODE ?? "development"

initializeStoredTheme()

const CLERK_STORAGE_MARKER = "salesdemon.clerk.publishable-key"

function resetStaleClerkState(currentKey: string | undefined) {
  if (typeof window === "undefined") return
  const storages: Storage[] = []
  try {
    storages.push(window.localStorage)
  } catch (error) {
    console.warn("localStorage unavailable while resetting Clerk state", error)
  }
  try {
    storages.push(window.sessionStorage)
  } catch (error) {
    console.warn("sessionStorage unavailable while resetting Clerk state", error)
  }

  const primaryStorage = storages[0]
  if (!primaryStorage) return

  const previousKey = (() => {
    try {
      return primaryStorage.getItem(CLERK_STORAGE_MARKER)
    } catch (error) {
      console.warn("Unable to read Clerk storage marker", error)
      return null
    }
  })()

  if (previousKey && previousKey === currentKey) {
    return
  }

  for (const storage of storages) {
    if (!storage) continue
    const keysToRemove: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key && key.toLowerCase().includes("clerk")) {
        keysToRemove.push(key)
      }
    }
    for (const key of keysToRemove) {
      try {
        storage.removeItem(key)
      } catch (error) {
        console.warn("Failed to remove Clerk storage key", key, error)
      }
    }
  }

  try {
    if (currentKey) {
      primaryStorage.setItem(CLERK_STORAGE_MARKER, currentKey)
    } else {
      primaryStorage.removeItem(CLERK_STORAGE_MARKER)
    }
  } catch (error) {
    console.warn("Unable to persist Clerk storage marker", error)
  }
}

if (!clerkPublishableKey && appMode !== "test" && appMode !== "e2e") {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable")
}

resetStaleClerkState(clerkPublishableKey)

if (typeof window !== "undefined" && (window as unknown as { Cypress?: unknown }).Cypress) {
  ;(window as typeof window & { __queryClient?: QueryClient }).__queryClient = queryClient
}

function AppFallback() {
  return (
    <div
      style={{
        padding: "48px",
        textAlign: "center",
        color: "var(--text)",
      }}
    >
      <h1 style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>Something glitched.</h1>
      <p style={{ maxWidth: 480, margin: "12px auto", color: "var(--muted)" }}>
        The Sales Demon team has been notified. Please refresh or try again in a moment.
      </p>
    </div>
  )
}

function App() {
  const resolvedClerkKey = clerkPublishableKey || "test_clerk_publishable_key"
  const appTree = (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DemoProvider>
          <RouterProvider router={router} />
        </DemoProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )

  return (
    <ClerkProvider
      publishableKey={resolvedClerkKey}
    >
      {appTree}
    </ClerkProvider>
  )
}

class RootErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error("Unhandled application error", error)
  }

  render() {
    return this.state.hasError ? <AppFallback /> : this.props.children
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>,
)
